"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.TransformMultiInstanceSchematic = void 0;
const _ = require("lodash");
const TransformObject_1 = require("./TransformObject");
const TransformSchematicModelInstanceConfiguration_1 = require("./TransformSchematicModelInstanceConfiguration");
const TransformMultiInstanceDisplayModule_1 = require("./TransformMultiInstanceDisplayModule");
const TransformSchematic_1 = require("./TransformSchematic");
class TransformMultiInstanceSchematic extends TransformObject_1.TransformObject {
    constructor(parent, args, netCombobulator, context) {
        super(parent, {
            ...args,
            schematics: new Set(),
            multiInstanceDisplayModules: new Set(),
        }, args.designId, new Set([
            TransformMultiInstanceDisplayModule_1.TransformMultiInstanceDisplayModule,
            TransformSchematic_1.TransformSchematic,
            TransformSchematicModelInstanceConfiguration_1.TransformSchematicModelInstanceConfiguration,
        ]), context);
        this.netCombobulator = netCombobulator;
        this._dependencies = [];
    }
    get designId() {
        return this._sharedData.designId;
    }
    get childSchematics() {
        return this._sharedData.schematics;
    }
    add(child) {
        this.assertCanAdd(child);
        this._dependencies.push(child);
        if (child instanceof TransformSchematic_1.TransformSchematic) {
            this._sharedData.schematics.add(child);
        }
        else if (child instanceof TransformMultiInstanceDisplayModule_1.TransformMultiInstanceDisplayModule) {
            this._sharedData.multiInstanceDisplayModules.add(child);
        }
    }
    compile() {
        const schematics = this.getAndVerifyChildSchematics();
        const modules = this.mergeModules(schematics);
        const nets = this.mergeNets(schematics);
        const drawings = this.mergeDrawings(schematics);
        const firstSchematic = schematics[0];
        const compiledMultiInstanceDisplayModules = Array.from(this._sharedData.multiInstanceDisplayModules).map((child) => child.compiled);
        const commonMetadata = this.findCommonMetadata(schematics);
        const multiInstanceConfiguration = {
            multiInstanceModules: compiledMultiInstanceDisplayModules,
            instances: schematics.map((s) => ({
                instanceSheetIndex: s.sheetIndex,
                instanceName: s.designName ?? s.designId,
                instanceId: s.designId,
                sheetDisplayPath: s.sheetDisplayPath,
                metadata: this.findUncommonMetadata(commonMetadata, s),
            })),
        };
        const model = {
            modelType: 'schematic',
            designId: this._sharedData.designId,
            designName: this._sharedData.designName,
            sourceFile: this._sharedData.sourceFile,
            sheetIndex: this._sharedData.sheetIndex,
            sheetDisplayPath: this._sharedData.sheetDisplayPath,
            layerSet: firstSchematic.layerSet,
            font: firstSchematic.font,
            grid: this._sharedData.grid,
            drawings,
            edges_bbox: this.mergeBboxes(schematics, 'edges_bbox'),
            initial_zoombox: this.mergeBboxes(schematics, 'initial_zoombox'),
            nets,
            modules,
            ports: {},
            metadata: this._sharedData.metadata,
            warnings: Array.from(new Set(schematics.flatMap((s) => s.warnings))),
            multiInstanceConfiguration,
        };
        return model;
    }
    findCommonMetadata(schematics) {
        const metadata = {};
        const [first, ...rest] = schematics;
        const includeKeys = new Set(Object.keys(first.metadata ?? {}));
        for (const other of rest) {
            for (const key of Object.keys(other.metadata ?? {})) {
                if (other.metadata?.[key] !== first.metadata?.[key]) {
                    includeKeys.delete(key);
                }
            }
            for (const key of includeKeys) {
                if (other.metadata?.[key] !== first.metadata?.[key]) {
                    includeKeys.delete(key);
                }
            }
        }
        for (const key of includeKeys) {
            metadata[key] = first.metadata?.[key];
        }
        return metadata;
    }
    findUncommonMetadata(commonMetadata, schematic) {
        const metadata = {};
        for (const [key, value] of Object.entries(schematic.metadata ?? {})) {
            if (commonMetadata[key] !== value) {
                metadata[key] = value;
            }
        }
        return metadata;
    }
    mergeDrawings(schematics) {
        const mergedDrawings = {};
        for (const schematic of schematics) {
            for (const [layerId, layerDrawings] of Object.entries(schematic.drawings)) {
                for (const drawing of layerDrawings) {
                    const drawingId = drawing[TransformSchematic_1.TRANSFORM_SCHEMATIC_DRAWING_ID_SYMBOL];
                    if (!drawingId) {
                        throw new Error(`Drawing without ID found in multi-instance schematic`);
                    }
                    if (!mergedDrawings[layerId]) {
                        mergedDrawings[layerId] = [];
                    }
                    const mergedDrawing = mergedDrawings[layerId].find((d) => {
                        const dId = d[TransformSchematic_1.TRANSFORM_SCHEMATIC_DRAWING_ID_SYMBOL];
                        return dId === drawingId;
                    });
                    if (!mergedDrawing) {
                        if (drawing.type === 'schref') {
                            mergedDrawings[layerId].push(drawing);
                            continue;
                        }
                        let nets = drawing.nets;
                        if (drawing.net) {
                            if (nets) {
                                throw new Error(`Drawing ${drawingId} has both net and nets properties set`);
                            }
                            const newNet = {
                                i: schematic.designId,
                                n: drawing.net,
                            };
                            nets = [newNet];
                        }
                        mergedDrawings[layerId].push({
                            ...drawing,
                            net: undefined,
                            nets,
                        });
                        continue;
                    }
                    if (drawing.type === 'schref') {
                        continue;
                    }
                    if (drawing.net) {
                        if (!mergedDrawing.nets) {
                            mergedDrawing.nets = [];
                        }
                        mergedDrawing.nets.push({
                            i: schematic.designId,
                            n: drawing.net,
                        });
                    }
                }
            }
        }
        return mergedDrawings;
    }
    mergeNets(schematics) {
        const nets = {};
        for (const schematic of schematics) {
            for (const [netName, net] of Object.entries(schematic.nets)) {
                if (netName in nets) {
                    nets[netName].connections.push(...net.connections);
                    if (net.diffPair) {
                        if (nets[netName].diffPair) {
                            if (!_.isEqual(nets[netName].diffPair.pairName, net.diffPair?.pairName)) {
                                throw new Error(`Conflicting differential pairs found for net ${netName}`);
                            }
                        }
                        else {
                            nets[netName].diffPair = net.diffPair;
                        }
                    }
                    if (net.metadata) {
                        nets[netName].metadata = {
                            ...(nets[netName].metadata ?? {}),
                            ...net.metadata,
                        };
                    }
                    if (net.localName) {
                        if (nets[netName].localName) {
                            if (nets[netName].localName !== net.localName) {
                                throw new Error(`Conflicting local names found for net ${netName}`);
                            }
                        }
                        else {
                            nets[netName].localName = net.localName;
                        }
                    }
                    if (net.localNames) {
                        if (nets[netName].localNames) {
                            nets[netName].localNames = Array.from(new Set([...nets[netName].localNames, ...net.localNames]));
                        }
                        else {
                            nets[netName].localNames = net.localNames;
                        }
                    }
                    if (net.netLabels) {
                        if (nets[netName].netLabels) {
                            nets[netName].netLabels.push(...net.netLabels);
                        }
                        else {
                            nets[netName].netLabels = net.netLabels;
                        }
                    }
                }
                else {
                    nets[netName] = net;
                }
            }
        }
        return nets;
    }
    mergeModules(schematics) {
        const moduleRefs = new Set();
        const modules = [];
        for (const schematic of schematics) {
            for (const module of schematic.modules) {
                if (moduleRefs.has(module.ref)) {
                    throw new Error(`Duplicate module ref ${module.ref} found in multi-instance schematic`);
                }
                moduleRefs.add(module.ref);
                modules.push(module);
            }
        }
        return modules;
    }
    mergeBboxes(schematics, field) {
        return {
            minx: Math.min(...schematics.map((s) => s[field].minx)),
            miny: Math.min(...schematics.map((s) => s[field].miny)),
            maxx: Math.max(...schematics.map((s) => s[field].maxx)),
            maxy: Math.max(...schematics.map((s) => s[field].maxy)),
        };
    }
    getAndVerifyChildSchematics() {
        const schematics = Array.from(this._sharedData.schematics).map((child) => child.compiled);
        if (schematics.length === 0) {
            throw new Error(`No child schematics found`);
        }
        const designIds = new Set(schematics.map((s) => s.designId));
        if (designIds.size !== schematics.length) {
            throw new Error(`Duplicate design ids found in child schematics`);
        }
        const [first, ...rest] = schematics;
        if (first.ports && Object.keys(first.ports).length > 0) {
            throw new Error(`Ports not yet supported on child schematics of a multi-instance schematic`);
        }
        for (const other of rest) {
            if (other.sourceFile !== first.sourceFile) {
                throw new Error(`All child schematics must have the same source file`);
            }
            else if (other.layerSet !== first.layerSet) {
                throw new Error(`All child schematics must have the same layer set`);
            }
            else if (!_.isEqual(other.ports, first.ports)) {
                throw new Error(`All child schematics must have the same ports`);
            }
        }
        return schematics;
    }
}
exports.TransformMultiInstanceSchematic = TransformMultiInstanceSchematic;
//# sourceMappingURL=TransformMultiInstanceSchematic.js.map